import { useEffect, useMemo, useState } from "react";

const formatCountdown = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const timeLabel = [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");

  return days > 0 ? `${days}d ${timeLabel}` : timeLabel;
};

export type SealTimeInput = string | number | Date | null | undefined;

/**
 * Parse API `seal_time` into epoch ms.
 * Accepts ISO strings, Date, unix seconds/ms (number or numeric string).
 */
export const parseSealDeadline = (seal_time?: SealTimeInput): number | null => {
  if (seal_time == null || seal_time === "") return null;

  if (seal_time instanceof Date) {
    const ms = seal_time.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  if (typeof seal_time === "number") {
    if (!Number.isFinite(seal_time) || seal_time <= 0) return null;
    return seal_time < 1e12 ? seal_time * 1000 : seal_time;
  }

  if (typeof seal_time !== "string") return null;

  const trimmed = seal_time.trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n < 1e12 ? n * 1000 : n;
  }

  const normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
  const parsed = Date.parse(normalized);
  if (Number.isFinite(parsed)) return parsed;

  const fallback = new Date(trimmed).getTime();
  return Number.isFinite(fallback) ? fallback : null;
};

export const SINGLE_CARD_SEAL_HOUR_OPTIONS = [24, 36, 48, 72] as const;
export const DEFAULT_SINGLE_CARD_SEAL_HOURS = 24;

export const computeSealTimeFromHours = (hours: number): string =>
  new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();

const useSealTimeGate = (seal_time?: SealTimeInput) => {
  const deadline_ms = useMemo(() => parseSealDeadline(seal_time), [seal_time]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (deadline_ms === null) return;

    const tick = () => setNowMs(Date.now());
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [deadline_ms]);

  const hasSealTime = deadline_ms !== null;
  const remainingMs = hasSealTime ? deadline_ms - nowMs : null;
  const isSealed = hasSealTime && deadline_ms <= nowMs;
  const isSealing = hasSealTime && !isSealed;

  return {
    showControls: !isSealed,
    hasSealTime,
    isSealing,
    isSealed,
    countdownLabel:
      isSealing && remainingMs != null ? formatCountdown(remainingMs) : null,
  };
};

export default useSealTimeGate;
